import React, { FC, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { Box, makeStyles, Typography } from '@material-ui/core';
import cx from 'classnames';
import { DialogComponent } from 'common/components';
import messages from 'common/components/PersonProfile/messages';
import commonMessages from 'common/messages/messages';
import { formatPrice } from 'common/utils';
import { getBalanceValue } from 'common/components/PersonProfile/utils';

const useStyles = makeStyles(theme => ({
  customPaper: {
    '& .MuiDialog-container>.MuiDialog-paperScrollPaper': {
      overflowY: 'hidden',
    },
  },

  block: {
    height: 'unset',
  },
  balanceItem: {
    margin: '8px 0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  currentBalance: {
    margin: '8px 0 20px',
    borderTop: '1px solid #d6d6d6',
    paddingTop: 8,
  },
  balanceItemValue: {
    fontSize: 14,
  },
  reward: {
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.warning.main,
    '&>svg ': {
      paddingRight: 4,
    },
  },
  payoff: {
    textAlign: 'center',
    marginTop: 16,
  },
}));

interface IBalanceSummary {
  pastDueCharges: number;
  pastDueFees: number;
  balance: number;
  pendingPayments: number;
  pendingWaivedFees: number;
  pendingRefunds: number;
  payoffIs: number;
}

interface IBillingScheduleModal {
  balanceSummary?: IBalanceSummary;
  onClose: () => void;
  getBalanceSummary: () => void;
  showBalanceForMember?: boolean;
}

const BalanceSummaryModal: FC<IBillingScheduleModal> = ({
  balanceSummary,
  onClose,
  getBalanceSummary,
  showBalanceForMember,
}) => {
  const classes = useStyles();

  useEffect(() => {
    getBalanceSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const calculatePayoff = (): number => {
    if (showBalanceForMember) {
      return (balanceSummary?.pastDueCharges || 0) + (balanceSummary?.pastDueFees || 0);
    } else {
      return (
        (balanceSummary?.pastDueCharges || 0) +
        (balanceSummary?.pastDueFees || 0) -
        (balanceSummary?.balance || 0)
      );
    }
  };

  return (
    <DialogComponent
      size="xs"
      isOpen
      title={<FormattedMessage {...messages.balanceSummary} />}
      submitBtnTitle={<FormattedMessage {...commonMessages.takePaymentBtn} />}
      onSubmit={() => null}
      onClose={onClose}
      className={classes.customPaper}
      disabled
    >
      <>
        <Box className={classes.balanceItem}>
          <Typography variant="body2" color="textSecondary" className={classes.balanceItemValue}>
            <FormattedMessage {...messages.chargesDue} />
          </Typography>
          <Typography component="span" color="error" className={classes.balanceItemValue}>
            {formatPrice(balanceSummary?.pastDueCharges || 0)}
          </Typography>
        </Box>
        <Box className={classes.balanceItem}>
          <Typography variant="body2" color="textSecondary" className={classes.balanceItemValue}>
            <FormattedMessage {...messages.feesDue} />
          </Typography>
          <Typography component="span" color="error" className={classes.balanceItemValue}>
            {formatPrice(balanceSummary?.pastDueFees || 0)}
          </Typography>
        </Box>
        <Box className={cx(classes.balanceItem, classes.currentBalance)}>
          <Typography variant="body2" color="textSecondary" className={classes.balanceItemValue}>
            <FormattedMessage {...messages.currentBalance} />
          </Typography>
          <Typography component="span" className={classes.balanceItemValue}>
            {getBalanceValue(balanceSummary?.balance || 0, showBalanceForMember)}
          </Typography>
        </Box>
        <Box className={classes.balanceItem}>
          <Typography variant="body2" color="textSecondary" className={classes.balanceItemValue}>
            <FormattedMessage {...messages.pendingPayments} />
          </Typography>
          <Typography component="span" className={classes.balanceItemValue}>
            {formatPrice(balanceSummary?.pendingPayments || 0)}
          </Typography>
        </Box>
        <Box className={classes.balanceItem}>
          <Typography variant="body2" color="textSecondary" className={classes.balanceItemValue}>
            <FormattedMessage {...messages.pendingWaivedFees} />
          </Typography>
          <Typography component="span" className={classes.balanceItemValue}>
            {formatPrice(balanceSummary?.pendingWaivedFees || 0)}
          </Typography>
        </Box>
        <Box className={classes.balanceItem}>
          <Typography variant="body2" color="textSecondary" className={classes.balanceItemValue}>
            <FormattedMessage {...messages.pendingRefunds} />
          </Typography>
          <Typography component="span" className={classes.balanceItemValue}>
            {formatPrice(balanceSummary?.pendingRefunds || 0)}
          </Typography>
        </Box>
        <Box className={classes.payoff}>
          <Typography className={classes.balanceItemValue}>
            <FormattedMessage
              {...messages.payoffIs}
              values={{
                value: formatPrice(calculatePayoff()),
              }}
            />
          </Typography>
        </Box>
      </>
    </DialogComponent>
  );
};

export default BalanceSummaryModal;
